/**
 * Study Assistant (legacy /api/v1/chat engine) artifacts, ported from pages/assistant:
 * status chips, detected column mapping, job created/running cards, market selection.
 */
import type { AssistantColumn } from "../../api";
import MarketSelectionArtifact from "../../../assistant/components/MarketSelectionArtifact";

export interface ArtifactItem {
  id: string;
  type: "column_mapping" | "job_created" | "job_running" | "market_selection";
  payload: any;
}

export const KNOWN_ARTIFACT_TYPES = ["column_mapping", "job_created", "job_running", "market_selection"];

const ROLE_COLORS: Record<string, string> = {
  media_spend: "#3B82F6", target: "#10B981", date: "#F59E0B", location: "#8B5CF6", unknown: "#64748B",
};

const JOB_ICONS: Record<string, string> = { MMM: "📈", GEO_LIFT: "🧪", BidOptimizer: "🎯", experiment: "🧪", mmm: "📈", bid: "🎯" };

const jobArea = (jobType: string) =>
  jobType === "MMM" ? "MMM" : jobType === "BidOptimizer" ? "Bid Optimizer" : "Experiments";

const STATUS_COLORS: Record<string, string> = { thinking: "var(--blue)", working: "var(--violet)", preparing: "var(--success-card)" };

export function StatusChip({ type, content }: { type: string; content: string }) {
  const color = STATUS_COLORS[type] ?? STATUS_COLORS.thinking;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
      style={{ color, borderColor: color }}
    >
      <span className="studio-pulse h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {content}
    </span>
  );
}

export function ColumnMappingArtifact({ payload, onConfirm }: { payload: any; onConfirm?: () => void }) {
  const cols: AssistantColumn[] = payload.columns || [];
  return (
    <div className="overflow-hidden rounded-card border border-border-default-t bg-surface">
      <div className="border-b border-border-subtle-t px-4 py-3 text-[13px] font-semibold text-primary-t">
        📋 Detected column mapping
      </div>
      <div className="px-4 py-3">
        <div className="grid grid-cols-[1fr_1fr_60px] border-b border-border-subtle-t pb-2 text-[10px] font-bold uppercase tracking-wider text-tertiary-t">
          <span>Column</span>
          <span>Detected role</span>
          <span>Conf.</span>
        </div>
        {cols.map((col) => (
          <div key={col.name} className="grid grid-cols-[1fr_1fr_60px] border-b border-border-subtle-t py-1.5">
            <span className="truncate font-mono text-xs text-primary-t">{col.name}</span>
            <span className="flex items-center gap-1.5 text-xs text-secondary-t">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: ROLE_COLORS[col.detected_role] || "#64748B" }} />
              {col.display_label || col.detected_role}
            </span>
            <span
              className="font-mono text-xs"
              style={{ color: col.confidence >= 0.8 ? "var(--success-card)" : "var(--warning)" }}
            >
              {Math.round(col.confidence * 100)}%
            </span>
          </div>
        ))}
        {onConfirm && (
          <button
            onClick={onConfirm}
            className="mt-3 w-full rounded-lg bg-adrsta-blue py-2 text-[13px] font-semibold text-white hover:opacity-90"
          >
            Confirm &amp; continue →
          </button>
        )}
      </div>
    </div>
  );
}

export function JobDetails({ payload, onView }: { payload: any; onView: (path: string) => void }) {
  return (
    <div className="rounded-card border p-5" style={{ borderColor: "var(--success-card)", background: "var(--success-card-subtle)" }}>
      <div className="mb-3 flex items-center gap-3">
        <span className="text-2xl">{JOB_ICONS[payload.job_type] || "⚡"}</span>
        <div>
          <div className="text-[14px] font-bold" style={{ color: "var(--success-card)" }}>Job created</div>
          <div className="text-xs text-secondary-t">{payload.job_name || "Unnamed job"}</div>
        </div>
      </div>
      <dl className="mb-3 space-y-1 text-xs">
        <div className="flex gap-2">
          <dt className="w-16 text-tertiary-t">Type</dt>
          <dd className="font-mono text-primary-t">{payload.job_type}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-16 text-tertiary-t">Job ID</dt>
          <dd className="font-mono text-secondary-t">{payload.job_config_id}</dd>
        </div>
      </dl>
      <p className="mb-3 flex items-center gap-1.5 text-xs" style={{ color: "var(--warning)" }}>
        <span className="studio-pulse h-1.5 w-1.5 rounded-full" style={{ background: "var(--warning)" }} />
        Running — this may take 5–20 minutes. You'll get a notification when it's ready.
      </p>
      {payload.view_url && (
        <button
          onClick={() => onView(payload.view_url)}
          className="w-full rounded-lg border py-2 text-[13px] font-semibold"
          style={{ borderColor: "var(--success-card)", color: "var(--success-card)" }}
        >
          View in {jobArea(payload.job_type)} →
        </button>
      )}
    </div>
  );
}

export function CompactJobCard({
  payload, onView, onDetails,
}: {
  payload: any;
  onView: (path: string) => void;
  onDetails: () => void;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-card border px-3 py-2.5"
      style={{ borderColor: "var(--success-card)", background: "var(--success-card-subtle)" }}
    >
      <span className="text-lg">{JOB_ICONS[payload.job_type] || "⚡"}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold" style={{ color: "var(--success-card)" }}>Job created</div>
        <div className="truncate text-xs text-secondary-t">{payload.job_name || "Unnamed job"}</div>
      </div>
      {payload.view_url && (
        <button
          onClick={() => onView(payload.view_url)}
          className="rounded-md border px-2.5 py-1 text-xs font-semibold"
          style={{ borderColor: "var(--success-card)", color: "var(--success-card)" }}
        >
          View →
        </button>
      )}
      <button
        onClick={onDetails}
        className="rounded-md border border-border-default-t px-2.5 py-1 text-xs text-secondary-t hover:bg-hover-bg"
      >
        Details →
      </button>
    </div>
  );
}

/** Inline artifact under an Study Assistant reply. `send` answers the design flow (e.g. market picks). */
export function InlineArtifact({
  artifact, send, onView, onDetails, interactive,
}: {
  artifact: ArtifactItem;
  send: (text: string) => void;
  onView: (path: string) => void;
  onDetails: (artifact: ArtifactItem) => void;
  interactive: boolean; // only the latest reply's controls should answer the flow
}) {
  switch (artifact.type) {
    case "column_mapping":
      return <ColumnMappingArtifact payload={artifact.payload} onConfirm={interactive ? () => send("looks good") : undefined} />;
    case "market_selection":
      return (
        <MarketSelectionArtifact
          payload={artifact.payload}
          onConfirm={(markets) => interactive && send(markets.length > 0 ? markets.join(", ") : "skip")}
        />
      );
    case "job_created":
    case "job_running":
      return <CompactJobCard payload={artifact.payload} onView={onView} onDetails={() => onDetails(artifact)} />;
    default:
      return null;
  }
}
